import { useMemo, useRef } from "react";
import styled from "styled-components";

import {
  SingleSelectDropdown,
  type SingleSelectDropdownSize,
} from "../dropdown/SingleSelectDropdown";
import {
  resolvePlaybackCapabilities,
  useVideoTimeline,
  type PlaybackTransport,
} from "./playbackTransport";
import {
  DEFAULT_PLAYBACK_RATES,
  normalizePlaybackRate,
  resolvePlaybackRateOptions,
} from "./playbackRate";

const PLAYBACK_RATE_CONTROL_WIDTH = "5.5rem";

const Root = styled.div`
  width: ${PLAYBACK_RATE_CONTROL_WIDTH};
  flex: 0 0 auto;
`;

export interface PlaybackRateSelectProps {
  transport: PlaybackTransport;
  label: string;
  rates?: readonly number[];
  size?: SingleSelectDropdownSize;
  disabled?: boolean;
  /** Rich timelines may return to playback after a pointer selection or Escape. */
  onReturnFocus?: (source: HTMLElement) => void;
}

/** 두 playback bar가 공유하는 transport-bound 배속 selector. Public package export는 아니다. */
export function PlaybackRateSelect({
  transport,
  label,
  rates = DEFAULT_PLAYBACK_RATES,
  size = "md",
  disabled = false,
  onReturnFocus,
}: PlaybackRateSelectProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const returnOnClose = useRef(false);
  const playbackRate = useVideoTimeline(transport, (snapshot) => snapshot.playbackRate);
  const playbackRateSupported = useVideoTimeline(
    transport,
    (snapshot) => resolvePlaybackCapabilities(snapshot).playbackRate,
  );
  const normalizedPlaybackRate = normalizePlaybackRate(playbackRate);
  const options = useMemo(
    () => resolvePlaybackRateOptions(rates, normalizedPlaybackRate),
    [normalizedPlaybackRate, rates],
  );

  return (
    <Root
      ref={rootRef}
      onPointerDownCapture={() => {
        returnOnClose.current = true;
      }}
      onKeyDownCapture={(event) => {
        returnOnClose.current = event.key === "Escape" && !event.nativeEvent.isComposing;
      }}
    >
      <SingleSelectDropdown
        label={label}
        onOpenChange={(open) => {
          if (open || !returnOnClose.current) return;
          returnOnClose.current = false;
          const trigger = rootRef.current?.querySelector<HTMLElement>("button");
          if (trigger) onReturnFocus?.(trigger);
        }}
        options={options}
        value={String(normalizedPlaybackRate)}
        onValueChange={(value) => transport.setPlaybackRate(Number(value))}
        size={size}
        fullWidth
        menuAlign="right"
        menuWidth={PLAYBACK_RATE_CONTROL_WIDTH}
        placement="top-end"
        disabled={disabled || !playbackRateSupported}
      />
    </Root>
  );
}

PlaybackRateSelect.displayName = "PlaybackRateSelect";
